// WalletContext.jsx
import { createContext, useState, useEffect, useCallback, useContext, useMemo, useRef } from 'react';
import { ethers } from "ethers";
import { getConstants, getNetwork } from '../utils/services';
import { createArtistContract } from '../contracts/artist';
import { useError } from './ErrorContextProvider';

export const WalletContext = createContext(null);

export const WalletProvider = ({ children }) => {
  const [provider, setProvider] = useState(null);
  const [account, setAccount] = useState(null);
  const [chainId, setChainId] = useState(null);
  const [artistContract, setArtistContract] = useState(null);
  const [apiConstants, setAPIConstants] = useState({});
  const { showError } = useError();

  const isConnectingRef = useRef(false);

  const connectWallet = useCallback(async () => {
    if (typeof window.ethereum === 'undefined') {
      showError('No wallet found. Please install MetaMask or Core.');
      return;
    }

    if (isConnectingRef.current) {
      console.log('Already connecting. Please wait.');
      return;
    }

    isConnectingRef.current = true;

    try {
      const _provider = new ethers.providers.Web3Provider(window.ethereum);
      await _provider.send("eth_requestAccounts", []);
      const signer = _provider.getSigner();
      const network = await _provider.getNetwork();
      const address = await signer.getAddress();
      const avalance = await getNetwork();

      if (parseInt(network.chainId) !== parseInt(avalance.chainId)) {
        console.error(`Network not supported: expected ${parseInt(avalance.chainId)} got ${parseInt(network.chainId)} network`);
        showError(`Failed: Network not supported. Connect to ${avalance.name}.`);
        return;
      }

      setProvider(_provider);
      setAccount(address);
      setChainId(parseInt(network.chainId));
      localStorage.setItem('walletConnected', 'true');
    } catch (err) {
      console.error('Connection error:', err);
    } finally {
      isConnectingRef.current = false;
    }
  }, [showError]);


  const disconnect = useCallback(() => {
    setAccount(null);
    setProvider(null);
    setChainId(null);
    localStorage.removeItem('walletConnected');
  }, []);

  const walletBalance = useCallback(async (address) => {
    try {
      if (!provider) throw new Error("Provider not available");
      const rawBalance = await provider.getBalance(address);
      return ethers.utils.formatEther(rawBalance);
    } catch (err) {
      console.error('Failed to fetch balance:', err);
    }
  }, [provider]);

  const sendTransaction = useCallback(async (to, value) => {
    try {
      if (!provider) throw new Error("Provider not available");
      const signer = provider.getSigner();
      const tx = await signer.sendTransaction({
        to,
        value: ethers.utils.parseEther(value),
      });
      return tx;
    } catch (err) {
      showError("Transaction failed. Please try again");
      console.error('Transaction error:', err);
      throw new Error("Transaction failed");
    }
  }, [provider, showError]);

  // Auto reconnect
  /*
  useEffect(() => {
    if (localStorage.getItem('walletConnected') === 'true') {
      connectWallet();
    }
  }, [connectWallet]);
  */

  // Handle wallet/account change
  useEffect(() => {
    if (!window.ethereum) return;

    const handleAccountsChanged = (accounts) => {
      disconnect();
      connectWallet();
    };

    const handleChainChanged = () => {
      window.location.reload();
    };

    window.ethereum.on('accountsChanged', handleAccountsChanged);
    window.ethereum.on('chainChanged', handleChainChanged);

    return () => {
      window.ethereum.removeListener('accountsChanged', handleAccountsChanged);
      window.ethereum.removeListener('chainChanged', handleChainChanged);
    };
  }, [disconnect]);

  useEffect(() => {
    const initializeContracts = async () => {
      if (provider) {
        const signer = provider.getSigner();
        const constants = await getConstants();
        const { contract } =  constants;
        const artistDeployedContract = createArtistContract(provider, signer, contract.artist);
        setArtistContract(artistDeployedContract);
        setAPIConstants(constants);
      }
    };
    initializeContracts();
  }, [provider]);

  // ✅ Memoize context value to avoid unnecessary re-renders
  const value = useMemo(() => ({
    provider,
    account,
    chainId,
    artistContract,
    apiConstants,
    connectWallet,
    disconnect,
    walletBalance,
    sendTransaction
  }), [provider, account, apiConstants, chainId, artistContract, connectWallet, disconnect, walletBalance, sendTransaction, showError]);

  return (
    <WalletContext.Provider value={value}>
      {children}
    </WalletContext.Provider>
  );
};

export const useWallet = () => {
  const context = useContext(WalletContext);
  if (!context) {
    throw new Error('useWallet must be used within a WalletProvider');
  }
  return context;
};
